import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { CloseButton, Fade } from '../components/common/Toast';
import { isProd } from '../helpers/inbdeUtils';
import LandingPage from '../pages/LandingPage';
import { prodCurrentUrl, prodOldUrl, qaCurrentUrl, qaOldUrl } from '../routes';

const handleRedirect = () => {
  const currentUrl = window.location.hostname;
  const currentPath = window.location.pathname;
  const isProdEnv = isProd();

  if (isProdEnv && prodOldUrl.includes(currentUrl)) {
    window.location = 'https://' + prodCurrentUrl + currentPath;
  }

  if (!isProdEnv && qaOldUrl.includes(currentUrl)) {
    window.location = 'https://' + qaCurrentUrl + currentPath;
  }
};

// an error is often the only copy of what went wrong, so it stays until it is closed (WCAG 2.2.1)
const showErrorToast = toast.error;
toast.error = (content, options) => showErrorToast(content, { autoClose: false, ...options });

const focusableSelector =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
  'textarea:not([disabled]), iframe, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

const isVisible = element => element.offsetWidth > 0 || element.offsetHeight > 0 || element.getClientRects().length > 0;

/*
reactstrap 8.0.1 dialogs set no aria-modal, and their Tab trap only wraps when focus is on the last
element of a list that includes hidden ones, so Tab could leave the dialog. One listener for the whole
app marks every open dialog as modal and keeps Tab and Shift+Tab inside the topmost one.
*/
const useModalDialogs = () =>
  useEffect(() => {
    const markDialogsModal = () =>
      document.querySelectorAll('.modal[role="dialog"]:not([aria-modal])').forEach(dialog => {
        dialog.setAttribute('aria-modal', 'true');
      });

    const keepTabInTopDialog = event => {
      const dialogs = document.querySelectorAll('.modal[role="dialog"]');
      const dialog = dialogs[dialogs.length - 1];
      if (event.key !== 'Tab' || !dialog) {
        return;
      }

      const items = [...dialog.querySelectorAll(focusableSelector)].filter(isVisible);
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      let target = null;

      if (!first) {
        target = dialog;
      } else if (!dialog.contains(active)) {
        target = event.shiftKey ? last : first;
      } else if (event.shiftKey && (active === first || active === dialog)) {
        target = last;
      } else if (!event.shiftKey && active === last) {
        target = first;
      }

      if (target) {
        event.preventDefault();
        target.focus();
      }
    };

    document.addEventListener('focusin', markDialogsModal);
    document.addEventListener('keydown', keepTabInTopDialog, true);

    return () => {
      document.removeEventListener('focusin', markDialogsModal);
      document.removeEventListener('keydown', keepTabInTopDialog, true);
    };
  }, []);

const Layout = () => {
  handleRedirect();
  useModalDialogs();

  return (
    <Router fallback={<span />}>
      <Switch>
        <Route path="/" component={LandingPage} />
      </Switch>
      <ToastContainer
        transition={Fade}
        closeButton={<CloseButton />}
        position={toast.POSITION.BOTTOM_LEFT}
        autoClose={10000}
      />
    </Router>
  );
};

export default Layout;
